import { useState, useEffect } from 'react';
import { useAuth } from "../../../contexts/AuthContext";
import { db, storage } from "../../../firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { doc, getDoc, setDoc } from "firebase/firestore";
import getServerTimestamp from '../../../functions/endpoints/GetServerTimestamp';
import { useNavigate, useParams } from "react-router";
import * as theme from '../../../theme';
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import ErrorDisplay from '../../../components/ErrorDisplay';
import { scanList } from '../../../contexts/FocusScanContext';
import dayjs from 'dayjs';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FileUploadContainer from '../../../components/fileUpload/FileUploadContainer';
import FileUploadButton from '../../../components/fileUpload/FileUploadButton';
import FileUploadLink from '../../../components/fileUpload/FileUploadLink';
import TruCard from '../../../components/TruCard';
import { extractPdf, extractPdfWellness } from '../../../functions/endpoints/ExtractPDF';
import { getScanDisplayLabel } from '../../../contexts/FocusScanContext';

export default function UploadScanFiles() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { clientId, scanId } = useParams();
  const [scan, setScan] = useState({});
  const [clientReport, setClientReport] = useState(null);
  const [rootCause, setRootCause] = useState(null);
  const [supplementShortened, setSupplementShortened] = useState(null);
  const [supplementDetail, setSupplementDetail] = useState(null);
  const [emotions, setEmotions] = useState(null);
  const [focusScans, setFocusScans] = useState([null, null, null, null]);
  const [focusScanTypes, setFocusScanTypes] = useState(['', '', '', '']);
  const [wellness, setWellness] = useState(null);
  const [dietGuide, setDietGuide] = useState(null);
  const [numberOfDrops, setNumberOfDrops] = useState('');
  const [numberOfWeeks, setNumberOfWeeks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sortedScanList = scanList
    .filter(scan => scan.label !== "No Preference")
    .sort((a, b) => a.label.localeCompare(b.label));

  const clearError = () => {
    setError('');
  };

  async function downloadFile(fileName) {
    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      setError('Popup blocked. Please allow popups for this site.');
      return;
    }

    try {
      const filePath = `clients/${clientId}/scans/${scanId}/${fileName}`;
      const fileRef = ref(storage, filePath);
      const url = await getDownloadURL(fileRef);
      newWindow.location.href = url;
    } catch (err) {
      setError('Failed to load file: ' + err.message);
      newWindow.close();
    }
  }

  // async function downloadFile(fileName) {
  //   try {
  //     const filePath = `clients/${clientId}/scans/${scanId}/${fileName}`;
  //     const fileRef = ref(storage, filePath);
  //     const url = await getDownloadURL(fileRef); // This respects Storage rules
  //     window.open(url, '_blank');
  //   } catch (err) {
  //     setError("Failed to load file: " + err.message);
  //   }
  // }

  useEffect(() => {
    const fetchScan = async () => {
      const scanRef = doc(db, "clients", clientId, "scans", scanId);
      const docSnap = await getDoc(scanRef);
      const scanData = docSnap.data() || {};
      setScan(scanData);

      if (scanData.reports) {
        setClientReport(scanData.reports.clientReport || null);
        setRootCause(scanData.reports.rootCause || null);
        setSupplementShortened(scanData.reports.supplementShortened || null);
        setSupplementDetail(scanData.reports.supplementDetail || null);
        setEmotions(scanData.reports.emotions || null);
        setFocusScans(scanData.reports.focusScans ? [...scanData.reports.focusScans, ...Array(4 - scanData.reports.focusScans.length).fill(null)] : [null, null, null, null]);
        setFocusScanTypes(scanData.reports.focusScans ? scanData.reports.focusScans.map(f => f?.focusScanType || '').concat(Array(4 - scanData.reports.focusScans.length).fill('')) : ['', '', '', '']);
        setWellness(scanData.reports.wellness || null);
        setDietGuide(scanData.reports.dietGuide || null);
        setNumberOfDrops(scanData.reports.numberOfDrops?.toString() || '');
        setNumberOfWeeks(scanData.reports.numberOfWeeks?.toString() || '');
      }
    };
    fetchScan();
  }, [clientId, scanId]);

  const handleFileChange = (type, index = null) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (type === 'focusScans') {
      const newFocusScans = [...focusScans];
      newFocusScans[index] = file;
      setFocusScans(newFocusScans);
    } else {
      switch (type) {
        case 'clientReport': setClientReport(file); break;
        case 'rootCause': setRootCause(file); break;
        case 'supplementShortened': setSupplementShortened(file); break;
        case 'supplementDetail': setSupplementDetail(file); break;
        case 'emotions': setEmotions(file); break;
        case 'wellness': setWellness(file); break;
        case 'dietGuide': setDietGuide(file); break;
        default: break;
      }
    }
  };

  const handleFocusScanTypeChange = (index) => (e) => {
    const newTypes = [...focusScanTypes];
    newTypes[index] = e.target.value;
    setFocusScanTypes(newTypes);
  };

  const validateUniqueFilenames = () => {
    const allFiles = [clientReport, rootCause, supplementShortened, supplementDetail, emotions, wellness, dietGuide, ...focusScans].filter(f => f);
    const allNames = allFiles.map(f => f.name || f.fileName);
    const uniqueNames = new Set(allNames);
    if (uniqueNames.size !== allNames.length) {
      setError("All file names must be unique within this scan.");
      return false;
    }
    return true;
  };

  const uploadFileAndVerify = async (file, storagePath, timestamp) => {
    if (!file) return null;
    if (file.fileName) return file; // already uploaded previously

    const fileRef = ref(storage, storagePath);

    // Step 1: Upload
    await uploadBytes(fileRef, file);

    // Step 2: Verify the file actually exists (with retry)
    const maxRetries = 5;
    let attempt = 0;
    while (attempt < maxRetries) {
      try {
        // This will throw if file doesn't exist or is not readable
        await getDownloadURL(fileRef); 
        // Or use getMetadata(fileRef) if you prefer
        break; // Success → exit retry loop
      } catch (err) {
        if (attempt === maxRetries - 1) throw err; // Final failure
        attempt++;
        // Exponential backoff
        await new Promise(res => setTimeout(res, 500 * Math.pow(2, attempt)));
      }
    }

    // Only reach here if file is confirmed present
    return {
      fileName: file.name,
      dateUploaded: timestamp,
      uploadedBy: currentUser.uid
    };
  };

  const handleDelete = async (type, index = null) => {
    setLoading(true);
    try {
      const scanRef = doc(db, "clients", clientId, "scans", scanId);
      if (type === 'focusScans') {
        const file = focusScans[index];
        if (file && file.fileName) {
          const fileRef = ref(storage, `clients/${clientId}/scans/${scanId}/${file.fileName}`);
          await deleteObject(fileRef);
        }
        const newFocusScans = [...focusScans];
        newFocusScans[index] = null;
        const newTypes = [...focusScanTypes];
        newTypes[index] = '';
        await setDoc(scanRef, { reports: { ...scan.reports, focusScans: newFocusScans.filter(f => f) } }, { merge: true });
        setFocusScans(newFocusScans);
        setFocusScanTypes(newTypes);
      } else {
        const file = { clientReport, rootCause, supplementShortened, supplementDetail, emotions, wellness, dietGuide }[type];
        if (file && file.fileName) {
          const fileRef = ref(storage, `clients/${clientId}/scans/${scanId}/${file.fileName}`);
          await deleteObject(fileRef);
        }
        await setDoc(scanRef, { reports: { ...scan.reports, [type]: null } }, { merge: true });

        switch (type) {
          case 'clientReport': setClientReport(null); break;
          case 'rootCause': setRootCause(null); break;
          case 'supplementShortened': setSupplementShortened(null); break;
          case 'supplementDetail': setSupplementDetail(null); break;
          case 'emotions': setEmotions(null); break;
          case 'wellness': setWellness(null); break;
          case 'dietGuide': setDietGuide(null); break;
          default: break;
        }
      }
    } catch (err) {
      setError("Failed to delete file: " + err.message);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateUniqueFilenames()) return;

    setLoading(true);
    setError("");

    let timestamp;
    let shouldExtractEmotions = false;
    let shouldExtractWellness = false;

    try {
      timestamp = await getServerTimestamp();
      const basePath = `clients/${clientId}/scans/${scanId}`;

      const isNewFileUpload = (currentFileObj, previousStoredFile) => {
        if (!currentFileObj) return false;
        if (currentFileObj instanceof File || currentFileObj instanceof Blob) return true;
        return false;
      };

      const previousEmotionsFile = scan.reports?.emotions || null;
      if (emotions && isNewFileUpload(emotions, previousEmotionsFile)) {
        shouldExtractEmotions = true;
      }

      const previousWellnessFile = scan.reports?.wellness || null;
      if (wellness && isNewFileUpload(wellness, previousWellnessFile)) {
        shouldExtractWellness = true;
      }

      const clientReportData = await uploadFileAndVerify(
        clientReport,
        `${basePath}/${clientReport?.name || ''}`,
        timestamp
      );
      const rootCauseData = await uploadFileAndVerify(
        rootCause,
        `${basePath}/${rootCause?.name || ''}`,
        timestamp
      );
      const supplementShortenedData = await uploadFileAndVerify(
        supplementShortened,
        `${basePath}/${supplementShortened?.name || ''}`,
        timestamp
      );
      const supplementDetailData = await uploadFileAndVerify(
        supplementDetail,
        `${basePath}/${supplementDetail?.name || ''}`,
        timestamp
      );
      const emotionsData = await uploadFileAndVerify(
        emotions,
        `${basePath}/${emotions?.name || ''}`,
        timestamp
      );
      const wellnessData = await uploadFileAndVerify(
        wellness,
        `${basePath}/${wellness?.name || ''}`,
        timestamp
      );
      const dietGuideData = await uploadFileAndVerify(
        dietGuide,
        `${basePath}/${dietGuide?.name || ''}`,
        timestamp
      );

      const focusScansData = await Promise.all(
        focusScans.map(async (file, i) => {
          if (!file || !focusScanTypes[i]) return null;

          // Preserve existing metadata + type if not a new file
          if (file.fileName) {
            return { ...file, focusScanType: focusScanTypes[i] };
          }

          return {
            ...(await uploadFileAndVerify(
              file,
              `${basePath}/${file.name}`,
              timestamp
            )),
            focusScanType: focusScanTypes[i]
          };
        })
      );

      const reports = {
        clientReport: clientReportData,
        rootCause: rootCauseData,
        supplementShortened: supplementShortenedData,
        supplementDetail: supplementDetailData,
        emotions: emotionsData,
        wellness: wellnessData,
        dietGuide: dietGuideData,
        focusScans: focusScansData.filter(Boolean),
        numberOfDrops: numberOfDrops ? Number(numberOfDrops) : null,
        numberOfWeeks: numberOfWeeks ? Number(numberOfWeeks) : null,
      };

      const scanRef = doc(db, "clients", clientId, "scans", scanId);
      await setDoc(scanRef, { reports, dateModified: timestamp }, { merge: true });

      if (shouldExtractEmotions && emotionsData) {
        const pdfPath = `${basePath}/${emotionsData.fileName}`;
        extractPdf({ clientId, scanId, pdfPath })
          .then(() => console.log("PDF extraction triggered successfully"))
          .catch(err => {
            console.error("Failed to trigger PDF extraction:", err);
          });
      }

      if (shouldExtractWellness && wellnessData) {
        const pdfPath = `${basePath}/${wellnessData.fileName}`;
        extractPdfWellness({ clientId, scanId, pdfPath })
          .then(() => console.log("PDF extraction triggered successfully"))
          .catch(err => {
            console.error("Failed to trigger PDF extraction:", err);
          });
      }

      navigate(`/clients/${clientId}/scans/${scanId}`);
    } catch (err) {
      console.error("Upload failed during verification:", err);
      setError(
        "One or more files failed to upload properly. Please try again. " +
        (err.message || err.code || "")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={theme.Container.basic}>
      <div style={theme.Heading}>Upload/Edit Scan Reports</div>
      <div style={theme.SubHeadingText}>Upload or replace reports for this scan. PDFs only. File names must be unique within this scan.</div>
      <Box sx={{ marginBottom: 2 }}>
        <ErrorDisplay
          message={error}
          type={'error'}
          clearMessage={clearError}
        />
      </Box>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
        
        <FileUploadContainer
          file={clientReport}
          fileName={'clientReport'}
          label={'Client Report'}
          showDelete={currentUser.role != 'practitioner'}
          onDelete={() => handleDelete('clientReport')}
          onUpload={() => handleFileChange('clientReport')}
          onClick={(fileName) => downloadFile(fileName)}
        />

        <FileUploadContainer
          file={wellness}
          fileName={'wellness'}
          label={'Wellness Scan'}
          showDelete={currentUser.role != 'practitioner'}
          onDelete={() => handleDelete('wellness')}
          onUpload={() => handleFileChange('wellness')}
          onClick={(fileName) => downloadFile(fileName)}
        />

        <FileUploadContainer
          file={dietGuide}
          fileName={'dietGuide'}
          label={'Diet Guide'}
          showDelete={currentUser.role != 'practitioner'}
          onDelete={() => handleDelete('dietGuide')}
          onUpload={() => handleFileChange('dietGuide')}
          onClick={(fileName) => downloadFile(fileName)}
        />

        <FileUploadContainer
          file={supplementDetail}
          fileName={'supplementDetail'}
          label={'Supplement (Detail)'}
          showDelete={currentUser.role != 'practitioner'}
          onDelete={() => handleDelete('supplementDetail')}
          onUpload={() => handleFileChange('supplementDetail')}
          onClick={(fileName) => downloadFile(fileName)}
        />

        <FileUploadContainer
          file={supplementShortened}
          fileName={'supplementShortened'}
          label={'Supplement (Shortened)'}
          showDelete={currentUser.role != 'practitioner'}
          onDelete={() => handleDelete('supplementShortened')}
          onUpload={() => handleFileChange('supplementShortened')}
          onClick={(fileName) => downloadFile(fileName)}
        />

        <FileUploadContainer
          file={rootCause}
          fileName={'rootCause'}
          label={'Root Cause'}
          showDelete={currentUser.role != 'practitioner'}
          onDelete={() => handleDelete('rootCause')}
          onUpload={() => handleFileChange('rootCause')}
          onClick={(fileName) => downloadFile(fileName)}
        />

        <FileUploadContainer
          file={emotions}
          fileName={'emotions'}
          label={'Emotions'}
          showDelete={currentUser.role != 'practitioner'}
          onDelete={() => handleDelete('emotions')}
          onUpload={() => handleFileChange('emotions')}
          onClick={(fileName) => downloadFile(fileName)}
        />

        <div style={{ ...theme.Container.flexColumn, width: '100%', gap: 20 }}>
          <div style={{ ...theme.Container.flexRow, gap: 20 }}>
            <TruCard title={'Focus Scans'} styles={{ width: '100%' }}>
              {focusScans.map((file, index) => (
                <div style={{ marginTop: index === 0 ? 0 : 18 }}>
                  {file ? (
                    <FileUploadLink 
                      key={file.fileName}
                      label={file.name || file.fileName}
                      content={dayjs(file.dateUploaded ? file.dateUploaded.toDate() : dayjs()).format('MM/DD/YYYY h:mm A')}
                      showDelete={currentUser.role != 'practitioner'}
                      onClick={() => downloadFile(file.fileName)}
                      onDelete={() => handleDelete('focusScans', index)}
                      focusScan={true}
                    >
                      <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Type</InputLabel>
                        <Select value={focusScanTypes[index]} onChange={handleFocusScanTypeChange(index)} label="Type">
                          <MenuItem value=""><em>None</em></MenuItem>
                          {sortedScanList.map((scan) => (<MenuItem key={scan.value} value={scan.value}>{getScanDisplayLabel(scan.value)}</MenuItem>))}
                        </Select>
                      </FormControl>
                    </FileUploadLink>
                  ) : (
                    <FileUploadButton
                      label={`Upload Focus Scan ${index + 1}`}
                      onChange={() => handleFileChange('focusScans', index)}
                    />
                  )}
                </div>
              ))}
            </TruCard>
          </div>
        </div>

        <div style={{ ...theme.Container.flexColumn, width: '100%', gap: 20 }}>
          <div style={{ ...theme.Container.flexRow, gap: 20 }}>
            <TruCard title={'Imprint Remedy Instructions'} styles={{ width: '100%' }}>
              <div style={{ ...theme.Container.flexRow, gap: 20 }}>
                <TextField
                  fullWidth
                  label="Number of Drops"
                  type="number"
                  value={numberOfDrops}
                  onChange={(e) => setNumberOfDrops(e.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                />
                <TextField
                  fullWidth
                  label="Number of Weeks"
                  type="number"
                  value={numberOfWeeks}
                  onChange={(e) => setNumberOfWeeks(e.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                />
              </div>
            </TruCard>
          </div>
        </div>

        <Box sx={{ display: 'flex', width: '100%', gap: 2, mt: 4 }}>
          <Button variant="contained" color="secondary" sx={{ flex: 1 }} onClick={() => navigate(`/clients/${clientId}/scans/${scanId}`)} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" sx={{ flex: 3 }} disabled={loading || (!clientReport && !rootCause && !supplementShortened && !supplementDetail && !emotions && !wellness && !dietGuide && !focusScans.some(f => f))}>
            {loading ? "Processing..." : "Save"}
          </Button>
        </Box>
      </Box>
    </div>
  );
}